<template>
  <v-container fluid>
    <TitleBar title="New Employee" />
    <v-form class="employee-form mt-5" @submit.prevent="submit">
      <EmployeeFormFields :employee="employee" :states="states" :countries="countries" :errors="formErrors"
        @touch="touchField" />

      <v-card outlined class="rounded-xl employee-form-section mb-5">
        <v-card-title class="employee-form-section__title">
          <v-icon color="primary" class="mr-3">mdi-store-outline</v-icon>
          <div>
            <div>Locations &amp; roles</div>
            <div class="employee-form-section__subtitle">Choose where this employee can work and what they can do there</div>
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-alert v-if="loadError" type="error" text role="alert">
            {{ loadError }}
            <v-btn text small color="error" @click="loadOptions">Retry</v-btn>
          </v-alert>
          <v-row align="start">
                    <v-col cols="12" md="5">
                      <v-autocomplete
                        outlined
                        label="Location"
                        :items="locations"
                        item-text="name"
                        v-model="selectedLocation"
                        return-object
                      >
                        <template #item="{ item }">
                          <span>{{ item.name }}</span>
                          <v-spacer />
                          <span v-if="item.Address">
                            {{ item.Address.addressLine1 }}
                          </span>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="5">
                      <v-autocomplete
                        outlined
                        label="Role at this location"
                        :items="roles"
                        item-text="name"
                        v-model="selectedRole"
                        return-object
                      />
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-btn
                        color="primary"
                        block
                        min-height="48"
                        :disabled="!selectedLocation || !selectedRole"
                        @click="addLocation"
                      >
                        Add assignment
                      </v-btn>
                    </v-col>
          </v-row>
          <div v-if="employee.Locations.length" class="assignment-list" role="list" aria-label="Assigned locations and roles">
            <div v-for="(item, index) in employee.Locations" :key="item.id" class="assignment-row" role="listitem">
              <v-icon color="primary" class="mr-3">mdi-store-outline</v-icon>
              <div class="assignment-row__content"><strong>{{ item.name }}</strong><span>{{ item.EmployeeRole && item.EmployeeRole.name }}</span></div>
              <v-btn icon :aria-label="`Remove ${item.name} assignment`" @click="deleteLocation(index)"><v-icon>mdi-delete-outline</v-icon></v-btn>
            </div>
          </div>
          <v-alert v-else text color="primary" icon="mdi-information-outline">No locations assigned yet.</v-alert>
        </v-card-text>
      </v-card>

      <div class="employee-form-actions">
        <v-btn text min-height="44" :disabled="saving" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" depressed min-height="44" type="submit" :loading="saving">Create employee</v-btn>
      </div>
    </v-form>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import employeeService from "../service";
import locationService from "../../Location/service.js";
import rolesService from "../modules/Roles/service.js";
import EmployeeFormFields from "./EmployeeFormFields.vue";

const { validationMixin } = require("vuelidate");
const {
  required,
  minLength,
  email,
  requiredIf,
} = require("vuelidate/lib/validators");

export default {
  name: "employee-create",
  components: { EmployeeFormFields },
  props: {
    isModal: Boolean,
  },
  created() {
    this.loadOptions();
  },
  data() {
    return {
      saving: false,
      loadError: "",
      locations: [],
      roles: [],
      selectedLocation: null,
      selectedRole: null,
      locationTable: {
        headers: [
          {
            text: "Name",
            value: "name",
            sortable: false,
          },
          {
            text: "Role",
            value: "EmployeeRole",
            sortable: false,
          },
          {
            text: "Action",
            value: "action",
            sortable: false,
            align: "center",
          },
        ],
      },
      employee: {
        firstName: null,
        lastName: null,
        pinCode: null,
        phoneNumber: null,
        email: null,
        website: null,
        note: null,
        Address: {},
        hourlyRate: null,
        Locations: [],
        metadata: {
          employeeHours: {
            breakHours: null,
            workingHours: null,
          },
          timeClockLogin: {
            admin: false,
            employee: false,
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters("global", [
      "states",
      "countries",
      "defaultCountryCode",
      "location",
    ]),
    // ...mapGetters('integration', ['locationModules', 'modules']),
    Address() {
      return this.employee.Address;
    },
    formErrors() {
      return {
        firstName: this.firstNameErrors,
        lastName: this.lastNameErrors,
        phoneNumber: this.phoneNumberErrors,
        email: this.emailErrors,
        pinCode: this.pinErrors,
        addressLine1: this.addressLine1Errors,
        city: this.cityErrors,
      };
    },
    pinErrors() {
      const errors = [];
      if (!this.$v.employee.pinCode.$dirty) return errors;
      if (!this.$v.employee.pinCode.required) errors.push("Field is required");
      if (!this.$v.employee.pinCode.minLength)
        errors.push(this.$t("validations.fieldMustbeatLeast4ChrLong"));
      return errors;
    },
    firstNameErrors() {
      const errors = [];
      if (!this.$v.employee.firstName.$dirty) return errors;
      if (!this.$v.employee.firstName.required)
        errors.push("Field is required");
      if (!this.$v.employee.firstName.minLength)
        errors.push(this.$t("validations.fieldMustbeatLeast2ChrLong"));
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.employee.lastName.$dirty) return errors;
      if (!this.$v.employee.lastName.minLength)
        errors.push(this.$t("validations.fieldMustbeatLeast2ChrLong"));
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.employee.phoneNumber.$dirty) return errors;
      // if (!this.$v.employee.phoneNumber.required) errors.push(this.$t('validations.fieldIsRequired'))
      if (!this.$v.employee.phoneNumber.minLength)
        errors.push(this.$t("validations.validLetters10"));
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.employee.email.$dirty) return errors;
      if (!this.$v.employee.email.email) errors.push("Email is not valid");
      return errors;
    },
    addressLine1Errors() {
      const errors = [];
      if (!this.$v.employee.Address.addressLine1.$dirty) return errors;
      if (!this.$v.employee.Address.addressLine1.minLength)
        errors.push(this.$t("validations.addressMustHaveAtleast2Characters"));
      if (!this.$v.employee.Address.addressLine1.required)
        errors.push("Field is required");
      return errors;
    },
    cityErrors() {
      const errors = [];
      if (!this.$v.employee.Address.city.$dirty) return errors;
      if (!this.$v.employee.Address.city.required)
        errors.push(this.$t("validations.cityIsRequired"));
      return errors;
    },
  },
  watch: {
    Address() {
      if (Object.keys(this.employee.Address).length > 0) {
        if (!this.employee.Address.city)
          this.employee.Address.city = this.location.Address.city;
        if (!this.employee.Address.StateId)
          this.employee.Address.StateId = this.location.Address.StateId;
        if (!this.employee.Address.CountryCode)
          this.employee.Address.CountryCode = this.location.Address.CountryCode;
      }
    },
  },
  methods: {
    async loadOptions() {
      this.loadError = "";
      try {
        this.roles = (await rolesService.getAll()).data;
        this.locations = (await locationService.getAll({ limit: 40 })).data;
      } catch (error) {
        this.loadError = "Locations and roles could not be loaded.";
      }
    },
    touchField(field) {
      if (field === "addressLine1" || field === "city") this.$v.employee.Address[field].$touch();
      else if (this.$v.employee[field]) this.$v.employee[field].$touch();
    },
    addLocation() {
      this.selectedLocation.EmployeeRole = this.selectedRole;
      this.employee.Locations = this.employee.Locations.concat(
        this.selectedLocation
      );
      // find the selected location and remove it from the list
      const foundIndex = this.locations.findIndex(
        (element) => this.selectedLocation.id === element.id
      );
      this.locations.splice(foundIndex, 1);
      this.selectedLocation = 0;
      this.selectedRole = 0;
    },
    deleteLocation(index) {
      this.locations.push(this.employee.Locations[index]);
      this.employee.Locations.splice(index, 1);
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            timer: 2000,
          })
          .fire({
            type: "error",
            title: "Invalid fields",
          });
      } else {
        this.saving = true;
        this.$Progress.start();
        console.log("sending employee", this.employee);
        employeeService
          .create(this.employee)
          .then(() => {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                timer: 2000,
              })
              .fire({
                type: "success",
                title: this.$t("toastMessage.text.employeeCreated"),
              });
            this.$Progress.finish();
            this.saving = false;
            this.close();
          })
          .catch((err) => {
            console.log(err);
            this.$swal
              .mixin({
                toast: true,
                showConfirmButton: true,
                position: "top-end",
                timer: 3000,
              })
              .fire({
                type: "error",
                title: err.data.message,
              });
            this.$Progress.fail();
            this.saving = false;
          });
      }
    },
    cancel() {
      this.close();
    },
    close() {
      if (!this.isModal) {
        this.$router.go(-1);
      } else {
        this.$emit("close");
      }
    },
  },
  mixins: [validationMixin],
  validations: {
    employee: {
      pinCode: {
        required,
        minLength: minLength(4),
      },
      firstName: {
        required,
        minLength: minLength(2),
      },
      lastName: {
        minLength: minLength(1),
      },
      phoneNumber: {
        minLength: minLength(10),
      },
      email: {
        email,
      },
      Address: {
        addressLine1: {
          required: requiredIf(
            (nestedModel) => Object.keys(nestedModel).length > 0
          ),
          minLength: minLength(2),
        },
        city: {
          required: requiredIf(
            (nestedModel) => Object.keys(nestedModel).length > 0
          ),
        },
      },
    },
  },
};
</script>

<style scoped>
.employee-form { max-width: 1180px; margin: 0 auto; padding-bottom: 88px; }
.employee-form-section { background: var(--app-surface, #fff); }
.employee-form-section__title { font-size: 1.05rem; line-height: 1.35; padding: 18px 20px; }
.employee-form-section__subtitle { color: var(--app-text-muted, rgba(0, 0, 0, 0.6)); font-size: .8125rem; font-weight: 400; margin-top: 2px; }
.assignment-list { border: 1px solid var(--app-border, rgba(0,0,0,.12)); border-radius: 12px; overflow: hidden; }
.assignment-row { align-items: center; display: flex; min-height: 58px; padding: 8px 12px; }
.assignment-row + .assignment-row { border-top: 1px solid var(--app-border, rgba(0,0,0,.12)); }
.assignment-row__content { display: flex; flex: 1; flex-direction: column; min-width: 0; }
.assignment-row__content span { color: var(--app-text-muted, rgba(0,0,0,.6)); font-size: .8125rem; }
.employee-form-actions { align-items: center; background: var(--app-surface, #fff); border-top: 1px solid var(--app-border, rgba(0,0,0,.12)); bottom: 0; display: flex; gap: 8px; justify-content: flex-end; margin: 0 -12px; padding: 12px 20px; position: sticky; z-index: 4; }
@media (max-width: 599px) { .employee-form-actions .v-btn { flex: 1; } }
</style>
